/**
 * Google's "G", drawn to their identity branding guidelines.
 *
 * https://developers.google.com/identity/branding-guidelines
 *
 * The geometry is `logo_googleg_48dp` verbatim — the same four paths Google
 * ships, on their own `0 0 48 48` grid, so the mark is neither redrawn nor
 * re-proportioned. Their guidelines forbid altering, recolouring, cropping or
 * distorting it, which is why nothing here is parameterised except the size.
 *
 * **The fills are tokens, and that is not a recolouring.** `--brand-google-*`
 * hold Google's exact values; they live in `tokens.css` because rule 1 puts
 * every colour literal there and the guard globs `.tsx` and `.svg` precisely so
 * an inline fill cannot slip past. `designTokens.test.ts` asserts they are
 * identical in both themes, which is what stops a dark-theme pass from deciding
 * four bright hues are too loud on a near-black card.
 *
 * **`aria-hidden`, always.** The mark never carries the accessible name — the
 * button's own text does. An `<svg role="img">` with a label here would make
 * every Google button announce "Google" twice.
 *
 * 18px is the guidelines' logo size for a 40px button and is the only size used
 * today; it is a prop rather than a constant because their spec scales the mark
 * with the button rather than fixing it.
 */
export function GoogleG({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      aria-hidden="true"
      focusable="false"
      /* `flex: none` so the mark cannot be squeezed by a long label in a flex
         row — a distorted G is exactly what the guidelines forbid. */
      style={{ flex: 'none' }}
    >
      <path
        fill="var(--brand-google-blue)"
        d="M47.532 24.5528c0-1.6362-.1454-3.2088-.4159-4.7179H24.48v9.0158h12.9204c-.5563 3.0006-2.2465 5.5442-4.7855 7.2452v6.0203h7.7511c4.5359-4.1774 7.1559-10.3318 7.1559-17.5634z"
      />
      <path
        fill="var(--brand-google-green)"
        d="M24.48 48c6.48 0 11.9134-2.1491 15.8845-5.8155l-7.7511-6.0203c-2.1491 1.44-4.8991 2.2909-8.1334 2.2909-6.2508 0-11.5443-4.2218-13.4327-9.8944H2.9182v6.2172C6.8673 42.5347 15.0367 48 24.48 48z"
      />
      <path
        fill="var(--brand-google-yellow)"
        d="M11.0473 28.5607c-.48-1.44-.7527-2.9782-.7527-4.5607s.2727-3.1207.7527-4.5607v-6.2172H2.9182C1.2934 16.4436.3818 20.1245.3818 24s.9116 7.5564 2.5364 10.7779l8.1291-6.2172z"
      />
      <path
        fill="var(--brand-google-red)"
        d="M24.48 9.5455c3.5236 0 6.6873 1.2109 9.1745 3.5891l6.8836-6.8836C36.3927 2.3891 30.96 0 24.48 0 15.0367 0 6.8673 5.4655 2.9182 13.4073l8.1291 6.2172C12.9357 13.7673 18.2292 9.5455 24.48 9.5455z"
      />
    </svg>
  )
}
